import React, { useEffect, useRef, useState } from "react";
import algoliasearch from "algoliasearch/lite";
import { InstantSearch, Configure } from "react-instantsearch";
import "instantsearch.css/themes/satellite.css";
import CustomSearchBox from "./components/CustomSearchBox";
import { InitialUiState, Routing } from "./StateMapping";
import { InsightsMiddleware } from "./Insights";
import AlgoliaSearchResults from "./components/AlgoliaSearchResults";
import { AnalyticsTags } from "./AnalyticsTags";
import { VirtualRefinementList } from "./components/VirtualRefinementList";
import CustomVirtualSearchBox from "./components/CustomVirtualSearchBox";
import { useSelector } from "react-redux";
import { AlgoliaPageLink } from "./components/types/AlgoliaPageLink";
import { QuickSearchBackLink } from "../QuickSearchToggle";
import { userTokenForPageLoad } from "./utils/UserToken";

interface AlgoliaSearchSettings {
  indexName: string;
  suggestionIndexName: string;
  sortByItems: any[];
  categoryPageId?: any;
  pageLinks?: AlgoliaPageLink[];
  insightsCategory?: string;
  ruleContexts?: string[];
}

interface AlgoliaInstantSearchSettings extends AlgoliaSearchSettings {
  className?: string;
  children: React.ReactNode;  
}

const AlgoliaInstantSearch = ({
  className,
  children,
  indexName,
  categoryPageId,
  insightsCategory,
  ruleContexts,
}: AlgoliaInstantSearchSettings) => {
  const searchClient = algoliasearch(
    window.__litium.algoliaSettings.appId,
    window.__litium.algoliaSettings.apiKey
  );
  const routingPrefix = "";
  const initialUiState = InitialUiState(
    indexName,
    routingPrefix,
    categoryPageId
  );

  return (
    <div className={`algolia-instant-search ${className || ""}`}>
      <InstantSearch
        searchClient={searchClient}
        indexName={indexName}
        future={{ preserveSharedStateOnUnmount: true }}
        routing={Routing(indexName, routingPrefix, categoryPageId)}
        insights={false}
        initialUiState={initialUiState}
      >
        <Configure ruleContexts={ruleContexts} />
        <InsightsMiddleware insightsCategory={insightsCategory} />
        <VirtualRefinementList attribute="categoryPageId" />
        <VirtualRefinementList attribute="brand" />
        <VirtualRefinementList attribute="filterSizesClothing" />
        <VirtualRefinementList attribute="filterSizesShoe" />
        <VirtualRefinementList attribute="filterSizesTrouser" />
        <VirtualRefinementList attribute="filterColor" />
        {children}
      </InstantSearch>
    </div>
  );
};

export const AlgoliaSearchResultsPage = (props: AlgoliaSearchSettings) => {
  const { indexName, suggestionIndexName, pageLinks } = props;
  const scrollToTop = (page: number) => {
    window.scrollTo(0, 0);
  };

  return (
    <AlgoliaInstantSearch {...props} insightsCategory={"Search"}>
      <Configure
        clickAnalytics={true}
        userToken={userTokenForPageLoad}
        analyticsTags={AnalyticsTags(["search"])}
        ruleContexts={props.ruleContexts}
      />
      <CustomVirtualSearchBox />
      <div className="algolia-search-results-page__container">
        <div className="algolia-search-results-page__search-box">
          <CustomSearchBox />
          <QuickSearchBackLink>
            <i className="ph-x"></i>
          </QuickSearchBackLink>
        </div>
        <div className="algolia-search-results-page__results-container">
          <AlgoliaSearchResults
            scrollToTop={scrollToTop}
            indexName={indexName}
            suggestionIndexName={suggestionIndexName}
            pageLinks={pageLinks}
          />
        </div>
      </div>
    </AlgoliaInstantSearch>
  );
};

export const AlgoliaQuickSearch = (props: AlgoliaSearchSettings) => {
  const quickSearch = useSelector((state) => state.quickSearch);
  const { indexName, suggestionIndexName, pageLinks } = props;
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const scrollToTop = (page: number) => {
    if (searchResultsRef.current) {
      searchResultsRef.current.scrollTo(0, 0);
    }
  };

  if (!quickSearch.active) {
    return null;
  }

  return (
    <AlgoliaInstantSearch
      {...props}
      className="algolia-quick-search"
      insightsCategory={"Search"}
    >
      <Configure
        clickAnalytics={true}
        userToken={userTokenForPageLoad}
        analyticsTags={AnalyticsTags(["search"])}
        ruleContexts={props.ruleContexts}
      />
      <CustomVirtualSearchBox />
      <div
        className="algolia-quick-search__results-container"
        ref={searchResultsRef}
      >
        <AlgoliaSearchResults
          scrollToTop={scrollToTop}
          indexName={indexName}
          suggestionIndexName={suggestionIndexName}
          pageLinks={pageLinks}
        />
      </div>
    </AlgoliaInstantSearch>
  );
};

interface AlgoliaCategoryPageSettings {
  categoryPageId: string;
  disableFilter: string;
  algoliaSettings: AlgoliaSearchSettings;
  analyticsTags?: string[];
  insightsCategory?: string;
  initialIndexName?: string;
  conditionJson?: string;
  algoliaPageFilter?: string;
}

export const AlgoliaCategoryPage = ({
  categoryPageId,
  disableFilter,
  algoliaSettings,
  insightsCategory,
  analyticsTags = [],
  initialIndexName,
  conditionJson,
  algoliaPageFilter,
}: AlgoliaCategoryPageSettings) => {
  const indexName = initialIndexName || algoliaSettings.indexName;
  const headerSize = 64;
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const scrollToTop = (page: number) => {
    if (searchResultsRef.current) {
      window.scrollTo(0, searchResultsRef.current.offsetTop - headerSize);
    }
  };

  let filters = "";
  if (conditionJson) {
    const conditions = JSON.parse(conditionJson);

    filters = Object.keys(conditions)
      .map((key) => {
        const value = conditions[key];

        if (typeof value === "string") {
          return `${key}:"${value}"`;
        } else {
          return `${key}:${value}`;
        }
      })
      .join(" AND ");
  }

  if (algoliaPageFilter) {
    filters = `(${algoliaPageFilter})`;
  }

  filters = [filters, "outOfStockAndOld:false"].filter(Boolean).join(" AND ");

  return (
    <AlgoliaInstantSearch
      {...algoliaSettings}
      indexName={indexName}
      categoryPageId={categoryPageId}
      insightsCategory={insightsCategory}
    >
      <Configure
        clickAnalytics={true}
        userToken={userTokenForPageLoad}
        analyticsTags={AnalyticsTags(analyticsTags)}
        filters={filters}
        ruleContexts={algoliaSettings.ruleContexts}
      />
      <div ref={searchResultsRef}>
        <AlgoliaSearchResults
          scrollToTop={scrollToTop}
          indexName={indexName}
          disabledFilters={[disableFilter, "categoryPageId"]}
        />
      </div>
    </AlgoliaInstantSearch>
  );
};

export const AlgoliaQuickSearchBox = () => {
  return (
    <div className="algolia-quick-search__searchbox">
      <CustomSearchBox />
    </div>
  );
};
